import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AssistantAvatar } from "../ui/assistantAvatar.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import { IssaQuickChat } from "../../pages/issaInsights/issaQuickChat.tsx";
import { buildAskRowPrompt, type AskRowColumn } from "./askRowPrompt.ts";
import { cellCopyText, rowCopyText } from "./rowCopy.ts";
import { useToast } from "../../hooks/useToast.ts";
import styles from "./rowContextMenu.module.scss";

export interface RowContextMenuProps {
  x: number;
  y: number;
  /** The page title users see, not its internal name. */
  pageTitle: string;
  /** The visible columns, each with the title the grid shows. */
  columns: AskRowColumn[];
  row: unknown[];
  colIndex: Record<string, number>;
  /** Column of the right-clicked cell; enables "Copy <column>". */
  column?: string;
  /** Each cell's displayed text by column, read from the row when it was clicked. */
  cellTexts: Record<string, string>;
  onClose: () => void;
}

export function RowContextMenu({ x, y, pageTitle, columns, row, colIndex, column, cellTexts, onClose }: RowContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [chatPrompt, setChatPrompt] = useState<string | null>(null);
  const assistant = useAssistant();
  const { toast } = useToast();

  const cellText = column ? cellCopyText(column, row, colIndex, cellTexts) : "";
  const cellTitle = column ? (columns.find((c) => c.name === column)?.title.trim() || "value") : "";

  async function copy(text: string, what: string) {
    onClose();
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} copied`, "success");
    } catch {
      toast("Could not copy to the clipboard", "error");
    }
  }

  // Render via the HTML Popover API (popover="manual") so the browser
  // places us in the top layer non-modally — necessary when the host grid
  // lives inside a `dialog.showModal()` Modal, which sits in the top layer
  // and would otherwise stack above any z-indexed portal sibling. (A plain
  // <dialog>.show() does *not* join the top layer; only showModal() and
  // popovers do, and showModal() would add a focus trap + backdrop that
  // make context-menu UX unworkable.)
  useEffect(() => {
    if (chatPrompt) return;
    const el = menuRef.current;
    if (!el) return;
    try {
      el.showPopover?.();
    } catch {
      // Browsers without popover support fall back to the in-flow div, which
      // works correctly outside modal contexts and degrades to behind-modal
      // inside them — same as before this change.
    }
    function onDocDown(e: MouseEvent) {
      if (el && !el.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
      try { el.hidePopover?.(); } catch { /* ignore */ }
    };
  }, [onClose, chatPrompt]);

  if (chatPrompt) {
    return <IssaQuickChat open prompt={chatPrompt} onClose={onClose} />;
  }

  // Clamp to viewport
  const mw = 260, mh = 130;
  const left = Math.min(x, window.innerWidth - mw - 4);
  const top = Math.min(y, window.innerHeight - mh - 4);

  return createPortal(
    <div
      ref={menuRef}
      popover="manual"
      className={styles.menu}
      style={{ left, top }}
      role="menu"
    >
      {cellText && (
        <button type="button" className={styles.item} role="menuitem" onClick={() => void copy(cellText, cellTitle)}>
          <span className={styles.iconSlot} aria-hidden="true" />
          <span className={styles.label}>Copy {cellTitle}</span>
        </button>
      )}
      <button type="button" className={styles.item} role="menuitem" onClick={() => void copy(rowCopyText(columns, row, colIndex, cellTexts), "Row")}>
        <span className={styles.iconSlot} aria-hidden="true" />
        <span>Copy row</span>
      </button>
      <button
        type="button"
        className={styles.item}
        role="menuitem"
        onClick={() => setChatPrompt(buildAskRowPrompt(pageTitle, columns, row, colIndex))}
      >
        <AssistantAvatar size={16} />
        <span>Ask {assistant.name} to explain this row</span>
      </button>
    </div>,
    document.body,
  );
}
